import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import type { AppVersionInfo } from '../hooks/useAppVersion';

interface VersionUpdateBannerProps {
  versionInfo: AppVersionInfo;
  onRefresh: () => void;
  onDismiss: () => void;
}

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b' };

const VersionUpdateBanner: React.FC<VersionUpdateBannerProps> = ({
  versionInfo,
  onRefresh,
  onDismiss,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [showNotes, setShowNotes] = useState(false);

  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';
  const textSecondary = isDark ? '#b0bec5' : '#546e7a';

  return (
    <Box
      role="alert"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.snackbar,
        px: { xs: 1, sm: 2 },
        pt: { xs: 1, sm: 1.5 },
      }}
    >
      <Alert
        severity="info"
        icon={false}
        sx={{
          bgcolor: surfaceBg,
          color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
          border: `1px solid ${panelBorder}`,
          borderLeft: `3px solid ${NAUTILUS.sand}`,
          borderRadius: '8px',
          boxShadow: 'none',
          alignItems: 'flex-start',
          '& .MuiAlert-message': {
            width: '100%',
            p: 0,
          },
        }}
        action={
          <IconButton
            aria-label="Dismiss update notice"
            size="small"
            onClick={onDismiss}
            sx={{
              color: textSecondary,
              mt: -0.5,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            width: '100%',
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              component="p"
              sx={{
                fontWeight: 600,
                fontSize: '0.9375rem',
                lineHeight: 1.4,
                mb: 0.5,
              }}
            >
              New version available
            </Typography>
            <Typography
              component="p"
              sx={{
                color: textSecondary,
                fontSize: '0.875rem',
                lineHeight: 1.5,
              }}
            >
              Refresh to load the update. Unsaved work on this page may be lost.
            </Typography>

            {versionInfo.notes.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => setShowNotes((open) => !open)}
                  endIcon={showNotes ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    px: 0,
                    minWidth: 0,
                    color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    '&:hover': {
                      bgcolor: alpha(isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, 0.08),
                    },
                  }}
                >
                  What&apos;s new
                </Button>
                <Collapse in={showNotes}>
                  <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                    {versionInfo.notes.map((note) => (
                      <Typography
                        key={note}
                        component="li"
                        sx={{
                          color: textSecondary,
                          fontSize: '0.875rem',
                          lineHeight: 1.5,
                          mb: 0.5,
                        }}
                      >
                        {note}
                      </Typography>
                    ))}
                  </Box>
                  {versionInfo.title && (
                    <Typography
                      sx={{
                        mt: 1,
                        color: textSecondary,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Release {versionInfo.title}
                    </Typography>
                  )}
                </Collapse>
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            sx={{
              alignSelf: { xs: 'flex-start', sm: 'center' },
              flexShrink: 0,
              bgcolor: NAUTILUS.sand,
              color: NAUTILUS.deepOcean,
              borderRadius: '8px',
              boxShadow: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              px: 2.5,
              py: 1,
              '&:hover': {
                bgcolor: alpha(NAUTILUS.sand, 0.85),
                boxShadow: 'none',
              },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Alert>
    </Box>
  );
};

export default VersionUpdateBanner;
